import type { SymbolImportBinding, SymbolTag } from '../../types.js';
import type { SymbolIndexFileEntry } from '../build/collect-file-entries.js';

const STAR_LOCAL_NAME = '*';

function isModuleLevelDef(tag: SymbolTag): boolean {
  return tag.role === 'def' && tag.parentScopeStartLine == null && tag.parentScopeStartColumn == null;
}

function collectExportableNamesFromTags(tags: readonly SymbolTag[]): Map<string, string> {
  const names = new Map<string, string>();
  for (const tag of tags) {
    if (!isModuleLevelDef(tag)) continue;
    if (!names.has(tag.name)) names.set(tag.name, tag.name);
  }
  return names;
}

export function existingLocalNames(imports: readonly SymbolImportBinding[]): Set<string> {
  return new Set(imports.map((binding) => binding.localName));
}

export function resolveExportableNames(
  filePath: string,
  entriesByPath: ReadonlyMap<string, SymbolIndexFileEntry>,
  visited: Set<string>,
): Map<string, { sourceName: string; sourceFilePath: string }> {
  if (visited.has(filePath)) return new Map();
  visited.add(filePath);

  const entry = entriesByPath.get(filePath);
  if (!entry) return new Map();

  const out = collectExportableNamesFromTags(entry.tags);
  const resolved = new Map<string, { sourceName: string; sourceFilePath: string }>();
  for (const [name, sourceName] of out) {
    resolved.set(name, { sourceName, sourceFilePath: filePath });
  }

  for (const binding of entry.imports) {
    if (binding.importKind !== 'star' || !binding.sourceFilePath) continue;
    const upstream = resolveExportableNames(binding.sourceFilePath, entriesByPath, visited);
    for (const [name, info] of upstream) {
      if (!resolved.has(name)) resolved.set(name, info);
    }
  }

  for (const binding of entry.imports) {
    if (binding.importKind !== 'reexport' && binding.importKind !== 'type') continue;
    if (!binding.sourceFilePath) continue;
    if (!resolved.has(binding.localName)) {
      resolved.set(binding.localName, {
        sourceName: binding.sourceName,
        sourceFilePath: binding.sourceFilePath,
      });
    }
  }

  return resolved;
}

export { STAR_LOCAL_NAME };
